import { Box } from '@mui/material'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  getGridStringOperators,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useOrganizationView } from 'api/aws/organizations_table/useOrganizationView'
import { useUserItems } from 'api/aws/users/useUserItems'
import ChildCategoryCell from 'components/DataGrid/ChildRows/ChildCategoryCell'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import {
  customDateSortOperator,
  customNumericSortOperator,
  customSortOperator,
} from 'components/DataGrid/operators/customSortOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import { useTreeExpansionPersistence } from 'components/DataGrid/useTreeExpansionPersistence'
import AggregateCurrencyCell from 'components/MTB/grid/cells/AggregateCell'
import RenderOrganizationCell from 'components/MTB/grid/cells/RenderOrganizationCell'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getOrgRowClassNames } from '../getOrgRowClassNames'
import { gridSxProps } from '../gridSxProps'
import '../OrganizationsPage.css'
import DateTimeCell from './DateTimeCell'
import OrganizationStatusCell from './OrganizationStatusCell'
import OwnerCell from './OwnerCell'
import TypeCell from './TypeCell'
import VerticalCell from './VerticalCell'

const defaultSortModel: GridSortModel = [{ field: 'name', sort: 'asc' }]
const defaultFilterModel: GridFilterModel = { items: [], quickFilterValues: [] }

export const initialVisibilityState: GridColumnVisibilityModel = {
  org_uuid: false,
  __tree_data_group__: false,
}

const RelatedOrgInfo = () => {
  const { PK } = useParams()
  const apiRef = useGridApiRef()
  const navigate = useNavigate()
  const { isExpanded, toggleExpanded } = useTreeExpansionPersistence('organizations')

  const { organization, isParent, childOrgs, relatedParentCompanies, isItemsLoading } = useOrganizationView(PK)
  const { data: users } = useUserItems()

  // Use childOrgs for parent organizations, relatedParentCompanies for child organizations
  const relatedOrgs = isParent ? childOrgs : relatedParentCompanies

  const processedRelatedOrgs = useMemo(() => {
    if (!Array.isArray(relatedOrgs) || !Array.isArray(users)) return []
    return relatedOrgs?.map(org => {
      const ownerId = org?.owner?.[0] || org?.owner || null
      const owner = ownerId ? users?.find(u => u?.user_uuid === ownerId) : null
      const orgId = org?.org_uuid || org?.PK
      return {
        ...org,
        owner: owner,
        isParent: true, // Mark all as parent rows for consistent rendering
        id: orgId, // Required for DataGrid
        rowId: org?.PK || `ORG_${orgId}`,
      }
    })
  }, [relatedOrgs, users])

  const densityStyles = getDensityStyles('standard')

  const handleRowClick = (row: GridRenderCellParams['row']) => {
    if (row?.PK) {
      navigate(`/organizations/${row.PK}/details`)
    }
  }
  const columnMap: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Organization Name',
      aggregable: false,
      width: 250,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_, row) => row?.name,
      renderCell: (params: GridRenderCellParams) => (
        <RenderOrganizationCell
          params={params}
          densityStyles={densityStyles}
          handleRowClick={handleRowClick}
          toggleExpanded={toggleExpanded}
        />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      aggregable: false,
      width: 150,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_, row) => (!row?.isParent ? row?.category : row?.type ?? ''),
      renderCell: (params: GridRenderCellParams) => {
        if (!params?.row?.isParent) return <ChildCategoryCell params={params} densityStyles={densityStyles} />
        return <TypeCell params={params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_, row) => row?.status ?? '',
      renderCell: (params: GridRenderCellParams) => (
        <OrganizationStatusCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'vertical',
      headerName: 'Vertical',
      width: 100,
      headerAlign: 'center',
      sortable: false,
      valueGetter: (_, row) => row?.vertical,
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => {
        return <VerticalCell params={params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'owner',
      headerName: 'Owner',
      width: 50,
      headerAlign: 'center',
      valueGetter: (_, row) => row?.owner?.name ?? '',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => {
        return <OwnerCell params={params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'creation_timestamp',
      headerName: 'Created Time',
      width: 75,
      headerAlign: 'center',
      valueGetter: (_value, row) => {
        return row?.creation_timestamp || null
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customDateSortOperator,
      renderCell: (params: GridRenderCellParams) => {
        return <DateTimeCell params={params} densityStyles={densityStyles} />
      },
    },
    {
      field: 'price',
      headerName: 'Price',
      aggregable: true,
      type: 'number',
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => parseFloat(row?.price ?? '0'),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'grossProfit',
      headerName: 'Gross Profit',
      aggregable: true,
      type: 'number',
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => parseFloat(row?.grossProfit ?? '0'),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'mediaSpend',
      headerName: 'Spend',
      aggregable: true,
      type: 'number',
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => parseFloat(row?.mediaSpend ?? '0'),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'netTotal',
      headerName: 'Net Total',
      aggregable: true,
      type: 'number',
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => parseFloat(row?.netTotal ?? '0'),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
  ]

  const defaultColumnWidths = Object.fromEntries(
    columnMap.filter(col => typeof col.width === 'number').map(col => [col.field, col.width])
  )
  const rows = useMemo(() => {
    if (!processedRelatedOrgs || processedRelatedOrgs?.length === 0) return []

    // For parent orgs viewing children: use tree data with products
    return processedRelatedOrgs
    // return buildOrgTreeRows(processedRelatedOrgs, organization?.products ?? [], organization?.deals ?? [])

    // For child orgs viewing parents: simple flat list (no tree)
  }, [processedRelatedOrgs, isParent, organization?.products, organization?.deals])
  if (isItemsLoading) {
    return <Box sx={{ p: 2 }}>Loading related organizations...</Box>
  }

  if (!relatedOrgs || relatedOrgs.length === 0) {
    return (
      <Box sx={{ p: 2, color: 'text.secondary' }}>
        {isParent ? 'No child organizations found.' : 'No parent organizations assigned.'}
      </Box>
    )
  }

  return (
    <GlobalDataGrid
      rows={rows}
      columns={columnMap}
      apiRef={apiRef}
      context={'related_orgs'}
      isExpanded={isExpanded}
      defaultFilterModel={defaultFilterModel}
      defaultVisibilityModel={initialVisibilityState}
      defaultSortModel={defaultSortModel}
      defaultGridDensity={'standard'}
      defaultColumnWidths={defaultColumnWidths}
      checkboxSelection={false}
      aggregationModel={{
        price: 'sum',
        grossProfit: 'sum',
        mediaSpend: 'sum',
        netTotal: 'sum',
      }}
      gridContainerSx={{
        position: 'relative',
        display: 'flex',
        maxHeight: '100vh',
        width: '100%',
        justifyContent: 'center',
        transition: 'all 0.3s ease-in-out',
        height: '100%',
      }}
      getRowClassName={getOrgRowClassNames}
      gridSxProps={gridSxProps}
      treeData={false}
    />
  )
}

export default RelatedOrgInfo
